import * as Sentry from "@sentry/react"
import React, {useEffect} from "react"
import ReactDOM from "react-dom/client"
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom"

import {App} from "./App.tsx"
import {ensureBrandFonts} from "./design/fonts/ensureBrandFonts.ts"
import {LandingPage} from "./entry-app/index.tsx"
import {API_BASE_URL, SENTRY_ENVIRONMENT, SKIP_LANDING_PAGE} from "./env.ts"
import {
  applySlideoutHostLayout,
  assignHostBridge,
  createFullscreenRootElement,
  createWidgetRootElement,
  dispatchHostEvent,
  getEntryAppElement,
  getHostBridge,
  getSlideoutHostElement,
  getWidgetMountElement,
  normalizeHostBridge,
  patchHostBridge,
  removeSlideoutHostLayout,
} from "./hostContract.ts"
import {ROOT} from "./routing.ts"
import {registerHostEventApi} from "./utils/trackEvent.ts"
import {emitHostSessionExited, emitHostSessionStarted} from "./utils/hostJourneyEvents.ts"
import {ChatWidget} from "./widget/ChatWidget.tsx"
import {
  ExpandWidgetInput,
  normalizeStarterBootstrapPayload,
} from "./widget/starterBootstrap.ts"
import {WIDGET_SLIDEOUT_WIDTH} from "./widget/styles.ts"

normalizeHostBridge()

let rootContainer: HTMLDivElement | undefined
let widgetContainer: HTMLDivElement | undefined
let root: ReactDOM.Root | undefined
let widgetRoot: ReactDOM.Root | undefined

const MOBILE_BREAKPOINT = 768

const applySlideoutLayout = (): void => {
  if (getHostBridge()?.displayMode !== "slideout") return
  if (window.innerWidth < MOBILE_BREAKPOINT) return
  applySlideoutHostLayout(WIDGET_SLIDEOUT_WIDTH)
}

const removeSlideoutLayout = (): void => {
  removeSlideoutHostLayout()
}

const initSentry = (): void => {
  if (SENTRY_ENVIRONMENT !== "development") {
    Sentry.init({
      dsn: "https://73f798bd169776b6bc7bc39ae2f6496e@o4506792914321408.ingest.sentry.io/4506813849927680",
      environment: SENTRY_ENVIRONMENT,
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0,
      tracePropagationTargets: [API_BASE_URL],
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Filtering
      beforeSend: (event, _hint) => {
        if (!event?.tags?.error_boundary) return null

        return event
      },
    })
  }
}

// ---- Full-screen app mode (legacy) ----

export const open = (initialMessage?: string): void => {
  if (root) {
    return
  }

  if (initialMessage) {
    // Pass via URL so LandingPage reads it with useSearchParams — no
    // dependency on host-bridge timing.
    window.location.hash = `${ROOT}?hostMsg=${encodeURIComponent(initialMessage)}`
  }

  if (!rootContainer) {
    rootContainer = createFullscreenRootElement()
  }

  // In testing, font imports seem to only work if they are in the
  // parent document, not inside the shadow DOM, so we have to add it
  // here instead of keeping it in <App /> with the rest of the
  // styles.
  ensureBrandFonts()

  // Prevent the host page scrolling while our modal is open
  document.body.style.top = `-${window.scrollY}px`
  document.body.style.position = "fixed"

  initSentry()

  emitHostSessionStarted()

  root = ReactDOM.createRoot(rootContainer)
  root.render(
    <React.StrictMode>
      <App onExit={close} />
    </React.StrictMode>,
  )
}

export const close = (): void => {
  if (!root) {
    return
  }

  emitHostSessionExited()

  root.unmount()
  root = undefined

  // Re-enable scrolling of the host page
  const scrollY = document.body.style.top
  document.body.style.position = ""
  document.body.style.top = ""
  window.scrollTo(0, parseInt(scrollY ?? "0") * -1)

  Sentry.close()
}

// `window.RepairIntelligence` is the one public integration object. The host
// defines its callbacks before the script loads; the full-screen bundle adds
// its controls once it has loaded. Keep `window.ri2` synchronized as a
// backwards-compatible alias for older integrations.
const registerFullscreenControls = (): void => {
  normalizeHostBridge()
  registerHostEventApi()
  const bridge = (getHostBridge() ?? {}) as RepairIntelligenceHostBridge
  bridge.open = open
  bridge.close = close
  assignHostBridge(bridge)
}

registerFullscreenControls()

// ---- Chat widget mode (new default embed) ----

export const mountWidget = (): void => {
  if (widgetRoot) {
    return
  }

  ensureBrandFonts()
  initSentry()
  const host = getHostBridge()
  if (!host?.startCollapsed) {
    applySlideoutLayout()
  }

  // In slideout mode, mount directly into the host element that the page layout
  // has reserved as a flex sibling of <section>. This lets Bootstrap containers
  // inside <section> genuinely reflow (flex + min-width:0) rather than being
  // overlapped by a fixed-position panel.
  const slideoutHost =
    host?.displayMode === "slideout" ? getSlideoutHostElement() : null

  if (slideoutHost) {
    widgetRoot = ReactDOM.createRoot(slideoutHost)
  } else {
    if (!widgetContainer) {
      widgetContainer = createWidgetRootElement()
    }
    widgetRoot = ReactDOM.createRoot(widgetContainer)
  }

  widgetRoot.render(
    <React.StrictMode>
      <ChatWidget />
    </React.StrictMode>,
  )
}

export const unmountWidget = (): void => {
  if (!widgetRoot) {
    return
  }

  widgetRoot.unmount()
  widgetRoot = undefined
  removeSlideoutLayout()
}

// ---- Widget collapse / expand (dispatches events that ChatWidget listens for) ----

export const collapseWidget = (): void => {
  dispatchHostEvent("collapse")
}

export const expandWidget = (
  initialMessageOrPayload?: ExpandWidgetInput,
): void => {
  const starterPayload = normalizeStarterBootstrapPayload(
    initialMessageOrPayload,
  )

  if (starterPayload) {
    patchHostBridge({
      initialMessage: starterPayload.starterMessage,
      initialStarterPayload: starterPayload,
    })
  }

  dispatchHostEvent("expand", {
    detail: starterPayload,
  })
}

// ---- Boot logic ----

const host = getHostBridge()
const headless = !!host?.headless
const entryAppElement = getEntryAppElement()
const widgetElement = getWidgetMountElement()

// Mount the chat widget if its container exists, or as the default
// when no other entry point is present.
// In headless mode the host page controls mounting entirely via RepairIntelligence.open().
if (!headless && (widgetElement || (!entryAppElement && !SKIP_LANDING_PAGE))) {
  mountWidget()
}

// Mount the full-screen app or landing page alongside the widget
if (SKIP_LANDING_PAGE) {
  open()
} else if (!headless && entryAppElement) {
  const landingPageRoot = ReactDOM.createRoot(entryAppElement)

  landingPageRoot.render(
    <React.StrictMode>
      <LandingPage open={open} />
    </React.StrictMode>,
  )
}
